import { useAuth0 } from '@auth0/auth0-react';
import { useGetTenant } from '@sunstone/api-client/hooks';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
  Separator,
} from '@sunstone/ui';
import { CircleHelp, ShieldCheck, UserCog } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { useIsServer } from '@/app/ServerContext';
import { useAuthorization, useAuthToken } from '@/features/auth';
import { routes } from '@/lib/routes';
import { useAppSelector } from '@/store';
import { ThemeToggle } from '../ui/ThemeToggle';
import { UniphoreLogo } from '../ui/UniphoreLogo';

const staticNavLinks = [
  { to: '/', label: 'Overview', exact: true },
  { to: '/partners', label: 'Partners' },
  { to: '/customers', label: 'Customers' },
  { to: '/deployments', label: 'Deployments' },
  { to: '/workspaces', label: 'Workspaces' },
  { to: '/releases', label: 'Releases' },
];

export function AppHeader() {
  const isServer = useIsServer();
  const location = useLocation();
  const { token } = useAuthToken();
  const {
    isPlatformAdmin,
    scope,
    tenantId: userTenantId,
    primaryRoleName,
    primaryRoleGrantsAll,
  } = useAuthorization();

  // Tenants nav link: platform-scope identity sees the full list; others go to their own tenant detail
  const tenantsNavTo =
    scope === 'all' ? '/customers' : userTenantId ? `/tenants/${userTenantId}` : '/';

  const navLinks = staticNavLinks
    .filter(({ to }) => {
      if (to === '/partners') return scope === 'all';
      return true;
    })
    .map((link) => {
      if (link.to !== '/customers') return link;
      const label = scope === 'all' ? 'Customers' : 'Home';
      return { ...link, to: tenantsNavTo, label };
    });

  // Parse tenantId from path to distinguish partner vs customer tenant detail pages
  const segments = location.pathname.split('/').filter(Boolean);
  const tenantId =
    segments[0] === 'tenants' && segments[1] && segments[1] !== 'new' ? segments[1] : '';
  const { data: tenantData } = useGetTenant(tenantId, token);
  // Whether the tenant currently being viewed (by URL) is a partner tenant
  const isViewingPartnerTenant = tenantId !== '' && !!tenantData && !tenantData.tenant?.parentId;

  let loginWithRedirect: (() => void) | undefined;
  let user: { email?: string; name?: string; picture?: string } | undefined;
  let auth0Logout: ((opts: { logoutParams: { returnTo: string } }) => void) | undefined;

  if (!isServer) {
    try {
      // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR guard — hook is only called on the client
      const auth = useAuth0();
      loginWithRedirect = auth.loginWithRedirect;
      user = auth.user;
      auth0Logout = auth.logout;
    } catch {
      // Auth0 not available (non-HTTPS context)
    }
  }

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  // Hide the brand logo on the home/welcome page where a larger logo is featured in the body.
  const isHomePage = location.pathname === '/';

  const isActive = (to: string, exact?: boolean) => {
    const p = location.pathname;
    if (exact) return p === to;
    switch (to) {
      case '/partners':
        return (
          (p.startsWith('/partners') || isViewingPartnerTenant) &&
          !p.includes('/deployments') &&
          !p.includes('/workspaces')
        );
      case '/customers':
        return (
          p.startsWith('/customers') ||
          (p.startsWith('/tenants') &&
            !p.includes('/deployments') &&
            !p.includes('/workspaces') &&
            !isViewingPartnerTenant)
        );
      case '/deployments':
        return p.includes('/deployments') && !p.includes('/workspaces');
      case '/workspaces':
        return p.includes('/workspaces');
      default:
        return p.startsWith(to);
    }
  };

  return (
    <header className="flex-none sticky top-0 z-50 bg-background border-b">
      <div className="max-w-7xl mx-auto px-4 h-14 flex items-center gap-6">
        {/* Brand */}
        <Link
          to="/"
          aria-label="Uniphore Business AI Cloud Management — home"
          className="flex items-center gap-3 transition-opacity hover:opacity-80"
        >
          {!isHomePage && <UniphoreLogo className="h-7" />}
          <span className="text-sm font-semibold text-foreground whitespace-nowrap hidden md:inline">
            Business AI Cloud Management
          </span>
        </Link>

        {/* Nav (signed-in only) */}
        {isAuthenticated && (
          <>
            <Separator orientation="vertical" className="h-5" />
            <NavigationMenu className="flex-1 max-w-none justify-start">
              <NavigationMenuList className="justify-start">
                {navLinks.map(({ to, label, exact }) => (
                  <NavigationMenuItem key={to}>
                    <NavigationMenuLink asChild data-active={isActive(to, exact) || undefined}>
                      <Link to={to} className={cn(navigationMenuTriggerStyle(), 'bg-transparent')}>
                        {label}
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          </>
        )}

        {/* Right side */}
        <div className="flex items-center gap-2 ml-auto">
          {isAuthenticated && (
            <Button variant="ghost" size="sm" asChild>
              <Link to="/help">
                <CircleHelp className="h-4 w-4" />
                Help
              </Link>
            </Button>
          )}
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full h-8 w-8">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="text-xs">
                      {user?.email?.[0]?.toUpperCase() ?? 'U'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="font-normal p-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9 shrink-0">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="text-sm font-medium">
                        {user?.email?.[0]?.toUpperCase() ?? 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      {user?.name && (
                        <p className="text-sm font-medium leading-tight truncate">{user.name}</p>
                      )}
                      <p className="text-xs text-muted-foreground truncate leading-tight mt-0.5">
                        {user?.email}
                      </p>
                      {primaryRoleName && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium tracking-wide uppercase text-muted-foreground border border-border rounded px-1.5 py-0.5">
                            <span
                              className={cn(
                                'h-1.5 w-1.5 rounded-full shrink-0',
                                primaryRoleGrantsAll
                                  ? isPlatformAdmin
                                    ? 'bg-foreground'
                                    : 'bg-muted-foreground'
                                  : 'bg-muted-foreground/50',
                              )}
                            />
                            {primaryRoleName}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="flex items-center justify-between rounded-sm px-2 py-1.5 text-sm">
                  <span className="text-sm">Appearance</span>
                  <ThemeToggle />
                </div>
                <DropdownMenuItem asChild>
                  <Link to="/preferences">Preferences</Link>
                </DropdownMenuItem>
                {isPlatformAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to={routes.platformUsers()}>
                        <UserCog className="h-4 w-4" />
                        Platform Users
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to={routes.systemRoles()}>
                        <ShieldCheck className="h-4 w-4" />
                        Roles
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  onClick={() =>
                    auth0Logout?.({ logoutParams: { returnTo: window.location.origin } })
                  }
                  className="text-destructive focus:text-destructive"
                >
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button size="sm" onClick={() => loginWithRedirect?.()}>
              Log in
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
